import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Paper,
    Chip,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    Button,
    TextField,
    InputAdornment,
    IconButton,
    Tooltip,
    Grid,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    Avatar,
    ButtonGroup
} from '@mui/material';
import {
    Search as SearchIcon,
    ChevronRight as ChevronRightIcon,
    AlertTriangle,
    CheckCircle,
    Clock,
    XCircle,
    Calendar,
    Filter,
    Download,
    User
} from 'react-feather';
import { RefreshCwIcon } from "lucide-react";
import { statusColors, defaultStatus, priorityColors, defaultPriority } from './mockData';
import CryptoJS from "crypto-js";
import { ApiDetails, KEY } from "../../Constants";

const TicketList = ({
                        tickets,
                        selectedTickets,
                        toggleTicketSelection,
                        toggleSelectAll,
                        openTicketDialog,
                        openBulkActionMenu,
                        openScheduleDialog,
                        showAlert
                    }) => {
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('All');
    const [priorityFilter, setPriorityFilter] = useState('All');
    const [propertyFilter, setPropertyFilter] = useState('All');
    const [categoryFilter, setCategoryFilter] = useState('All');
    const [assigneeFilter, setAssigneeFilter] = useState('All');
    const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
    const [dateRangeFilter, setDateRangeFilter] = useState({
        startDate: '',
        endDate: ''
    });

    // Add/modify these methods for filtering
    const handleSearchChange = (event) => {
        setSearchTerm(event.target.value);
        // Debounce the search if applying immediately
    };

    // Export CSV function
    const exportToCSV = async () => {
        try {
            // Use the filtered tickets list
            const ticketsToExport = selectedTickets.length > 0
                ? tickets.filter(ticket => selectedTickets.includes(ticket.id))
                : filteredTickets;

            let csvContent = "data:text/csv;charset=utf-8,";
            csvContent += "ID,Title,Description,Category,Priority,Status,Created,Updated,Tenant,Property,Assigned To\n";

            ticketsToExport.forEach(ticket => {
                const row = [
                    ticket.id,
                    `"${ticket.title?.replace(/"/g, '""') || ''}"`,
                    `"${ticket.description?.replace(/"/g, '""') || ''}"`,
                    ticket.problem_type_name || '',
                    ticket.priority || '',
                    ticket.status || '',
                    ticket.date_created ? new Date(ticket.date_created).toLocaleDateString() : '',
                    ticket.updatedAt ? new Date(ticket.updatedAt).toLocaleDateString() : '',
                    ticket.tenant_name || '',
                    ticket.property_name || '',
                    ticket.assigned_to_name || 'Unassigned'
                ];
                csvContent += row.join(',') + '\n';
            });

            const encodedUri = encodeURI(csvContent);
            const link = document.createElement("a");
            link.setAttribute("href", encodedUri);
            link.setAttribute("download", `tickets_export_${new Date().toISOString().slice(0, 10)}.csv`);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

            showAlert("Tickets exported successfully", "success");
        } catch (error) {
            console.error("Error exporting tickets:", error);
            showAlert("Failed to export tickets", "error");
        }
    };

    // Handle page change
    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    // Handle rows per page change
    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    // Handle status filter change
    const handleStatusFilterChange = (event) => {
        setStatusFilter(event.target.value);
        setPage(0);
    };

    // Handle priority filter change
    const handlePriorityFilterChange = (event) => {
        setPriorityFilter(event.target.value);
        setPage(0);
    };

    // Handle property filter change
    const handlePropertyFilterChange = (event) => {
        setPropertyFilter(event.target.value);
        setPage(0);
    };

    // Handle category filter change
    const handleCategoryFilterChange = (event) => {
        setCategoryFilter(event.target.value);
        setPage(0);
    };

    // Handle assignee filter change
    const handleAssigneeFilterChange = (event) => {
        setAssigneeFilter(event.target.value);
        setPage(0);
    };

    // Handle date range filter
    const handleDateRangeChange = (field, value) => {
        setDateRangeFilter({
            ...dateRangeFilter,
            [field]: value
        });
        setPage(0);
    };

    // Toggle advanced filters
    const toggleAdvancedFilters = () => {
        setShowAdvancedFilters(!showAdvancedFilters);
    };

    // Reset all filters
    const resetFilters = () => {
        setSearchTerm('');
        setStatusFilter('All');
        setPriorityFilter('All');
        setPropertyFilter('All');
        setCategoryFilter('All');
        setAssigneeFilter('All');
        setDateRangeFilter({
            startDate: '',
            endDate: ''
        });
        setPage(0);
    };

    // Filter tickets based on search term and filters - FIXED for case insensitivity
    const filteredTickets = tickets.filter(ticket => {
        // For debugging, log the ticket status and the current filter status when they don't match
        if (statusFilter !== 'All' && ticket.status &&
            ticket.status.toLowerCase() !== statusFilter.toLowerCase() &&
            ticket.status.trim().toLowerCase() !== statusFilter.trim().toLowerCase()) {
            console.log(`Status filter mismatch - Filter: "${statusFilter}", Ticket status: "${ticket.status}"`);
        }

        // Basic information check to avoid errors
        if (!ticket) return false;

        // Search term filter - safely access properties
        const matchesSearch = searchTerm === '' || (
            (ticket.problem_name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
            (ticket.status || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
            (ticket.description || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
            ((ticket.tenant?.first_name || '') + ' ' + (ticket.tenant?.last_name || '')).toLowerCase().includes(searchTerm.toLowerCase()) ||
            (ticket.tenant?.house_number || '').toLowerCase().includes(searchTerm.toLowerCase())
        );

        // Case-insensitive status filter
        const matchesStatus = statusFilter === 'All' ||
            (ticket.status && ticket.status.toLowerCase() === statusFilter.toLowerCase());

        // Case-insensitive priority filter
        const matchesPriority = priorityFilter === 'All' ||
            (ticket.priority && ticket.priority.toLowerCase() === priorityFilter.toLowerCase());

        // Case-insensitive property filter
        const matchesProperty = propertyFilter === 'All' ||
            (ticket.property_name && ticket.property_name === propertyFilter);

        // Case-insensitive category filter
        const matchesCategory = categoryFilter === 'All' ||
            (ticket.problem_type_name && ticket.problem_type_name === categoryFilter);

        // Advanced filters
        const matchesAssignee = assigneeFilter === 'All' ||
            (assigneeFilter === 'Unassigned' && !ticket.assignedTo) ||
            (ticket.assignedTo?.id === assigneeFilter);

        // Date range filter
        let matchesDateRange = true;
        if (dateRangeFilter.startDate) {
            const startDate = new Date(dateRangeFilter.startDate);
            const ticketDate = new Date(ticket.date_created || ticket.createdAt);
            matchesDateRange = ticketDate >= startDate;
        }
        if (dateRangeFilter.endDate && matchesDateRange) {
            const endDate = new Date(dateRangeFilter.endDate);
            const ticketDate = new Date(ticket.date_created || ticket.createdAt);
            matchesDateRange = ticketDate <= endDate;
        }

        return matchesSearch && matchesStatus && matchesPriority &&
            matchesProperty && matchesCategory && matchesAssignee && matchesDateRange;
    });

    // Get unique properties
    const uniqueProperties = [...new Set(tickets.map(ticket => ticket.property_name))].filter(Boolean);

    // Get unique categories
    const uniqueCategories = [...new Set(tickets.map(ticket => ticket.problem_type_name))].filter(Boolean);

    // Get unique statuses for debugging and consistent filtering
    const uniqueStatuses = [...new Set(tickets.map(ticket => ticket.status))].filter(Boolean);

    // Paginated tickets
    const paginatedTickets = filteredTickets.slice(
        page * rowsPerPage,
        page * rowsPerPage + rowsPerPage
    );

    // Safe getter for status colors
    const getStatusColor = (status) => {
        return statusColors[status] || defaultStatus;
    };

    // Safe getter for priority colors
    const getPriorityColor = (priority) => {
        return priorityColors[priority] || defaultPriority;
    };

    // Get staff initials for avatar
    const getInitials = (name) => {
        if (!name) return '?';
        return name.split(' ').map(n => n[0]).join('').toUpperCase();
    };

    // Format date
    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        try {
            return new Date(dateString).toLocaleString('en-US', {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
            });
        } catch (error) {
            console.error("Date formatting error:", error);
            return 'Invalid date';
        }
    };

    // Get status icon component
    const getStatusIcon = (status) => {
        const iconType = statusColors[status]?.icon || defaultStatus.icon;
        switch (iconType) {
            case 'AlertTriangle':
                return <AlertTriangle size={14}/>;
            case 'Clock':
                return <Clock size={14}/>;
            case 'CheckCircle':
                return <CheckCircle size={14}/>;
            case 'XCircle':
                return <XCircle size={14}/>;
            case 'Calendar':
                return <Calendar size={14}/>;
            default:
                return <AlertTriangle size={14}/>;
        }
    };

    return (
        <>
            {/* Header section */}
            <Box sx={{mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                <Box>
                    <Typography variant="h5" sx={{fontWeight: 600, mb: 1}}>
                        Manage Support Tickets
                    </Typography>
                    <Typography variant="body1" color="textSecondary">
                        View and manage all tenant support requests
                    </Typography>
                </Box>
                <Box>
                    <ButtonGroup variant="outlined" sx={{mr: 1}}>
                        <Button
                            startIcon={<Download size={16}/>}
                            onClick={exportToCSV}
                            sx={{borderRadius: '8px', textTransform: 'none'}}
                        >
                            Export
                        </Button>
                    </ButtonGroup>
                    <Button
                        variant="contained"
                        color="primary"
                        sx={{borderRadius: '8px', textTransform: 'none'}}
                        disabled={selectedTickets.length === 0}
                        onClick={openBulkActionMenu}
                    >
                        Bulk Actions ({selectedTickets.length})
                    </Button>
                </Box>
            </Box>

            {/* Filters section */}
            <Paper
                elevation={0}
                sx={{
                    mb: 3,
                    p: 2,
                    borderRadius: '10px',
                    border: '1px solid rgba(0, 0, 0, 0.08)'
                }}
            >
                <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} md={4}>
                        <TextField
                            fullWidth
                            placeholder="Search tickets, tenants, units..."
                            value={searchTerm}
                            onChange={handleSearchChange}
                            variant="outlined"
                            size="small"
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon size={18}/>
                                    </InputAdornment>
                                ),
                                sx: {borderRadius: '8px'}
                            }}
                        />
                    </Grid>

                    <Grid item xs={12} sm={6} md={2}>
                        <FormControl fullWidth size="small">
                            <InputLabel id="status-filter-label">Status</InputLabel>
                            <Select
                                labelId="status-filter-label"
                                id="status-filter"
                                value={statusFilter}
                                onChange={handleStatusFilterChange}
                                label="Status"
                                sx={{borderRadius: '8px'}}
                            >
                                <MenuItem value="All">All Statuses</MenuItem>
                                {/* Use actual status values from tickets instead of hardcoded values */}
                                {uniqueStatuses.map(status => (
                                    <MenuItem key={status} value={status}>{status.toString()}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>

                    <Grid item xs={12} sm={6} md={2}>
                        <FormControl fullWidth size="small">
                            <InputLabel id="priority-filter-label">Priority</InputLabel>
                            <Select
                                labelId="priority-filter-label"
                                id="priority-filter"
                                value={priorityFilter}
                                onChange={handlePriorityFilterChange}
                                label="Priority"
                                sx={{borderRadius: '8px'}}
                            >
                                <MenuItem value="All">All Priorities</MenuItem>
                                <MenuItem value="High">High</MenuItem>
                                <MenuItem value="Medium">Medium</MenuItem>
                                <MenuItem value="Low">Low</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>

                    <Grid item xs={12} sm={6} md={2}>
                        <FormControl fullWidth size="small">
                            <InputLabel id="property-filter-label">Property</InputLabel>
                            <Select
                                labelId="property-filter-label"
                                id="property-filter"
                                value={propertyFilter}
                                onChange={handlePropertyFilterChange}
                                label="Property"
                                sx={{borderRadius: '8px'}}
                            >
                                <MenuItem value="All">All Properties</MenuItem>
                                {uniqueProperties.map(property => (
                                    <MenuItem key={property} value={property}>{property}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>

                    <Grid item xs={12} sm={6} md={2} sx={{display: 'flex', justifyContent: 'flex-end'}}>
                        <Tooltip title="Reset filters">
                            <IconButton
                                onClick={resetFilters}
                                disabled={
                                    !searchTerm &&
                                    statusFilter === 'All' &&
                                    priorityFilter === 'All' &&
                                    propertyFilter === 'All' &&
                                    categoryFilter === 'All' &&
                                    assigneeFilter === 'All' &&
                                    !dateRangeFilter.startDate &&
                                    !dateRangeFilter.endDate
                                }
                            >
                                <RefreshCwIcon size={20}/>
                            </IconButton>
                        </Tooltip>
                    </Grid>
                </Grid>

                {/* Advanced filters */}
                {showAdvancedFilters && (
                    <Grid container spacing={2} alignItems="center" sx={{mt: 1}}>
                        <Grid item xs={12} sm={6} md={3}>
                            <FormControl fullWidth size="small">
                                <InputLabel id="category-filter-label">Category</InputLabel>
                                <Select
                                    labelId="category-filter-label"
                                    id="category-filter"
                                    value={categoryFilter}
                                    onChange={handleCategoryFilterChange}
                                    label="Category"
                                    sx={{borderRadius: '8px'}}
                                >
                                    <MenuItem value="All">All Categories</MenuItem>
                                    {uniqueCategories.map(category => (
                                        <MenuItem key={category} value={category}>{category}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>

                        <Grid item xs={12} sm={6} md={3}>
                            <FormControl fullWidth size="small">
                                <InputLabel id="assignee-filter-label">Assigned To</InputLabel>
                                <Select
                                    labelId="assignee-filter-label"
                                    id="assignee-filter"
                                    value={assigneeFilter}
                                    onChange={handleAssigneeFilterChange}
                                    label="Assigned To"
                                    sx={{borderRadius: '8px'}}
                                >
                                    <MenuItem value="All">All Staff</MenuItem>
                                    <MenuItem value="Unassigned">Unassigned</MenuItem>
                                    {/* Staff menu items would be populated here */}
                                </Select>
                            </FormControl>
                        </Grid>

                        <Grid item xs={12} sm={6} md={3}>
                            <TextField
                                fullWidth
                                label="From Date"
                                type="date"
                                size="small"
                                value={dateRangeFilter.startDate}
                                onChange={(e) => handleDateRangeChange('startDate', e.target.value)}
                                InputLabelProps={{shrink: true}}
                                sx={{borderRadius: '8px'}}
                            />
                        </Grid>

                        <Grid item xs={12} sm={6} md={3}>
                            <TextField
                                fullWidth
                                label="To Date"
                                type="date"
                                size="small"
                                value={dateRangeFilter.endDate}
                                onChange={(e) => handleDateRangeChange('endDate', e.target.value)}
                                InputLabelProps={{shrink: true}}
                                sx={{borderRadius: '8px'}}
                            />
                        </Grid>
                    </Grid>
                )}
            </Paper>

            {/* Tickets table */}
            <Paper
                elevation={0}
                sx={{
                    borderRadius: '10px',
                    border: '1px solid rgba(0, 0, 0, 0.08)',
                    overflow: 'hidden'
                }}
            >
                {filteredTickets.length === 0 ? (
                    <Box sx={{py: 6, textAlign: 'center'}}>
                        <AlertTriangle size={48} color="#9CA3AF"/>
                        <Typography variant="h6" sx={{mt: 2}}>No tickets found</Typography>
                        <Typography variant="body2" color="textSecondary" sx={{mt: 1}}>
                            {tickets.length > 0
                                ? 'Try changing your search filters'
                                : 'There are no support tickets in the system'}
                        </Typography>
                    </Box>
                ) : (
                    <>
                        <TableContainer>
                            <Table sx={{minWidth: 650}}>
                                <TableHead sx={{backgroundColor: '#F9FAFB'}}>
                                    <TableRow>
                                        <TableCell padding="checkbox">
                                            <Tooltip title="Select All">
                                                <IconButton
                                                    size="small"
                                                    onClick={() => toggleSelectAll(filteredTickets)}
                                                >
                                                    {selectedTickets.length === filteredTickets.length ?
                                                        <CheckCircle size={16}/> :
                                                        <div style={{
                                                            width: 16,
                                                            height: 16,
                                                            border: '1px solid #6B7280',
                                                            borderRadius: '2px'
                                                        }}/>
                                                    }
                                                </IconButton>
                                            </Tooltip>
                                        </TableCell>
                                        <TableCell sx={{fontWeight: 600}}>Issue</TableCell>
                                        <TableCell sx={{fontWeight: 600}}>Tenant/Unit</TableCell>
                                        <TableCell sx={{fontWeight: 600}}>Property</TableCell>
                                        <TableCell sx={{fontWeight: 600}}>Category</TableCell>
                                        <TableCell sx={{fontWeight: 600}}>Priority</TableCell>
                                        <TableCell sx={{fontWeight: 600}}>Status</TableCell>
                                        <TableCell sx={{fontWeight: 600}}>Assigned To</TableCell>
                                        <TableCell sx={{fontWeight: 600}}>Created</TableCell>
                                        <TableCell align="right" sx={{fontWeight: 600}}>Actions</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {paginatedTickets.map((ticket) => (
                                        <TableRow
                                            key={ticket.id}
                                            hover
                                            sx={{
                                                '&:last-child td, &:last-child th': {border: 0},
                                                cursor: 'pointer',
                                                '&:hover': {backgroundColor: '#F9FAFB'}
                                            }}
                                            selected={selectedTickets.includes(ticket.id)}
                                            onClick={() => openTicketDialog(ticket)}
                                        >
                                            <TableCell padding="checkbox" onClick={(e) => {
                                                e.stopPropagation();
                                                toggleTicketSelection(ticket.id);
                                            }}>
                                                <IconButton size="small">
                                                    {selectedTickets.includes(ticket.id) ?
                                                        <CheckCircle size={16}/> :
                                                        <div style={{
                                                            width: 16,
                                                            height: 16,
                                                            border: '1px solid #6B7280',
                                                            borderRadius: '2px'
                                                        }}/>
                                                    }
                                                </IconButton>
                                            </TableCell>
                                            <TableCell>
                                                <Tooltip title={ticket.description || ''} placement="top">
                                                    <Typography
                                                        variant="body2"
                                                        sx={{
                                                            maxWidth: 250,
                                                            whiteSpace: 'nowrap',
                                                            overflow: 'hidden',
                                                            textOverflow: 'ellipsis'
                                                        }}
                                                    >
                                                        {ticket.problem_name || 'No title'}
                                                    </Typography>
                                                </Tooltip>
                                            </TableCell>
                                            <TableCell>
                                                <Box sx={{display: 'flex', alignItems: 'center'}}>
                                                    <Avatar
                                                        sx={{
                                                            width: 24,
                                                            height: 24,
                                                            fontSize: '0.75rem',
                                                            mr: 1,
                                                            bgcolor: '#3B82F6'
                                                        }}
                                                    >
                                                        {getInitials((ticket.tenant?.first_name || '') + " " + (ticket.tenant?.last_name || ''))}
                                                    </Avatar>
                                                    <Box>
                                                        <Typography variant="body2" sx={{lineHeight: 1.2}}>
                                                            {(ticket.tenant?.first_name || '') + " " + (ticket.tenant?.last_name || '')}
                                                        </Typography>
                                                        <Typography variant="caption" color="textSecondary">
                                                            {ticket.tenant?.house_number || 'N/A'}
                                                        </Typography>
                                                    </Box>
                                                </Box>
                                            </TableCell>
                                            <TableCell>{ticket.property_name || 'N/A'}</TableCell>
                                            <TableCell>{ticket.problem_type_name || 'N/A'}</TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={ticket.priority || 'N/A'}
                                                    size="small"
                                                    sx={{
                                                        backgroundColor: getPriorityColor(ticket.priority).bg,
                                                        color: getPriorityColor(ticket.priority).color,
                                                        borderRadius: '4px',
                                                        fontSize: '0.75rem',
                                                        fontWeight: 500
                                                    }}
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <Chip
                                                    icon={getStatusIcon(ticket.status)}
                                                    label={ticket.status || 'N/A'}
                                                    size="small"
                                                    sx={{
                                                        backgroundColor: getStatusColor(ticket.status).bg,
                                                        color: getStatusColor(ticket.status).color,
                                                        borderRadius: '4px',
                                                        fontSize: '0.75rem',
                                                        fontWeight: 500
                                                    }}
                                                />
                                            </TableCell>
                                            <TableCell>
                                                {ticket.assignedTo ? (
                                                    <Box sx={{display: 'flex', alignItems: 'center'}}>
                                                        <Avatar
                                                            sx={{
                                                                width: 24,
                                                                height: 24,
                                                                fontSize: '0.75rem',
                                                                mr: 1,
                                                                bgcolor: '#10B981'
                                                            }}
                                                        >
                                                            {getInitials((ticket.assignedTo.first_name || '') + " " + (ticket.assignedTo.last_name || ''))}
                                                        </Avatar>
                                                        <Typography variant="body2">
                                                            {(ticket.assignedTo.first_name || '') + " " + (ticket.assignedTo.last_name || '')}
                                                        </Typography>
                                                    </Box>
                                                ) : (
                                                    <Typography variant="body2" color="textSecondary">
                                                        Unassigned
                                                    </Typography>
                                                )}
                                            </TableCell>
                                            <TableCell>{formatDate(ticket.date_created)}</TableCell>
                                            <TableCell align="right">
                                                <IconButton size="small" onClick={(e) => {
                                                    e.stopPropagation();
                                                    openTicketDialog(ticket);
                                                }}>
                                                    <ChevronRightIcon size={18}/>
                                                </IconButton>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                        <TablePagination
                            rowsPerPageOptions={[10, 25, 50]}
                            component="div"
                            count={filteredTickets.length}
                            rowsPerPage={rowsPerPage}
                            page={page}
                            onPageChange={handleChangePage}
                            onRowsPerPageChange={handleChangeRowsPerPage}
                        />
                    </>
                )}
            </Paper>
        </>
    );
};

export default TicketList;